// components.jsx — shared atoms: icons, SourceGraph, SourceCard, brand mark.
import { sourceNavigationCandidates, isLocatedSource } from "./source-navigation.js";
import { loadSourcePreview, sourcePreviewSegments } from "./source-preview.js";
const { useState, useEffect, useRef, useMemo } = React;

// ── Icons (1.6 stroke, currentColor) ─────────────────────────────────────────
const Ic = {
  search: "M11 4a7 7 0 105.2 11.7L21 20.5 22.5 19l-4.8-4.8A7 7 0 0011 4zm0 2a5 5 0 110 10 5 5 0 010-10z",
  book:   "M5 4h9a3 3 0 013 3v13a1 1 0 01-1.4.9C13.9 19.6 11 19 9 19H5a1 1 0 01-1-1V5a1 1 0 011-1zm9 2H6v11h3c1.7 0 3.6.4 5 .9V7a1 1 0 00-1-1z",
  contents:"M3.5 5.5c2.8-.9 5.7-.2 8.5 1.7v13c-2.8-1.9-5.7-2.6-8.5-1.7v-13zm17 0c-2.8-.9-5.7-.2-8.5 1.7v13c2.8-1.9 5.7-2.6 8.5-1.7v-13zM6 9h3m-3 4h3m6-4h3m-3 4h3",
  graph:  "M6 13a3 3 0 100 6 3 3 0 000-6zm12-9a3 3 0 100 6 3 3 0 000-6zm0 9a3 3 0 100 6 3 3 0 000-6zM8.6 14.4l6-6m-6 3.2l6 6",
  compass:"M12 2a10 10 0 100 20 10 10 0 000-20zm3.7 6.3l-2 5.4-5.4 2 2-5.4 5.4-2z",
  layers: "M12 3l9 5-9 5-9-5 9-5zm9 9l-9 5-9-5m18 4l-9 5-9-5",
  spark:  "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z",
  close:  "M6 6l12 12M18 6L6 18",
  chev:   "M9 6l6 6-6 6",
  chevd:  "M6 9l6 6 6-6",
  plus:   "M12 5v14M5 12h14",
  note:   "M5 4h14v11l-5 5H5V4zm9 16v-4h4",
  trans:  "M4 5h7M7 5c0 4-2 7-4 9m1-4c1 2 3 4 5 5m6-9h3l3 9m-7 0h8m-6-4h4",
  text:   "M5 5h14M5 5v3m14-3v3M12 5v14m-3 0h6",
  compare:"M9 4v16M4 8l5-4 5 4M20 16l-5 4-5-4",
  bookmark:"M7 4h10v16l-5-3.5L7 20V4z",
  external:"M14 5h5v5M19 5l-7 7M11 5H6a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1v-5",
  back:   "M15 6l-6 6 6 6",
  sliders:"M5 8h9m3 0h2M5 16h2m3 0h9M14 5v6M10 13v6",
  globe:  "M12 2a10 10 0 100 20 10 10 0 000-20zm0 0c3 3 3 17 0 20M2 12h20",
  quote:  "M7 7h4v4c0 2-1 4-3 5M14 7h4v4c0 2-1 4-3 5",
  path:   "M5 19a3 3 0 100-6 3 3 0 000 6zm14-8a3 3 0 100-6 3 3 0 000 6zm-3 3c-6 0-6-5-11-5",
  printer:"M6 9V4a1 1 0 011-1h10a1 1 0 011 1v5 M6 18H5a2 2 0 01-2-2v-3a2 2 0 012-2h14a2 2 0 012 2v3a2 2 0 01-2 2h-1 M7 15h10v6H7z",
  check:  "M5 12l5 5L20 6",
  link:   "M10 13a5 5 0 007.1 0l2-2a5 5 0 00-7.1-7.1l-1.1 1.1M14 11a5 5 0 00-7.1 0l-2 2a5 5 0 007.1 7.1l1.1-1.1",
  copy:   "M8 8H5a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-3M10 3h9a2 2 0 012 2v9a2 2 0 01-2 2h-9a2 2 0 01-2-2V5a2 2 0 012-2z",
  menu:   "M4 7h16M4 12h16M4 17h16",
  // Filled circles: a zero-length stroke with round caps disappears at icon
  // sizes, which made the reader's overflow control almost invisible.
  dots:   "M4.25 12a1.75 1.75 0 013.5 0 1.75 1.75 0 01-3.5 0zM10.25 12a1.75 1.75 0 013.5 0 1.75 1.75 0 01-3.5 0zM16.25 12a1.75 1.75 0 013.5 0 1.75 1.75 0 01-3.5 0z",
  user:   "M12 12a4 4 0 100-8 4 4 0 000 8zm-7 9a7 7 0 0114 0",
  sun:    "M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10l1.4 1.4m0-12.8l-1.4 1.4m-10 10L5.6 18.4M12 7a5 5 0 100 10 5 5 0 000-10z",
  moon:   "M20.5 14.2A8.5 8.5 0 019.8 3.5 8.5 8.5 0 1020.5 14.2z",
  brightness: "M12 3a9 9 0 100 18 9 9 0 000-18zm0 0v18M12 3a9 9 0 010 18",
};
function Icon({ d, size = 18, sw = 1.6, fill, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill || "none"}
      stroke={fill ? "none" : "currentColor"} strokeWidth={sw} strokeLinecap="round"
      strokeLinejoin="round" style={style} aria-hidden="true">
      <path d={Ic[d] || d} />
    </svg>
  );
}

// ── Mayanos brand mark ───────────────────────────────────────────────────────
function Brand({ size = 28, withText = true }) {
  return (
    <div className="brand" style={{ display: "flex", alignItems: "center", gap: 11 }}>
      <img className="brand-logo-img" src="/assets/mayanos-logo-192.png" width={size} height={size} alt="" aria-hidden="true" />
      {withText && (
        <div style={{ lineHeight: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-.02em", color: "var(--ink)" }}>Mayanos</div>
        </div>
      )}
    </div>
  );
}

// ── Source-type dot ───────────────────────────────────────────────────────────
const typeVar = (t) => `var(--t-${(window.SOURCE_TYPES[t] || {}).key || "rebbe"})`;
function TypeDot({ type, size = 8 }) {
  return <span className="dot" style={{ width: size, height: size, background: typeVar(type) }} />;
}

function sourceDisplayTitle(src) {
  const title = String(src && src.title || "").trim();
  return /^dach\s*:/i.test(title) ? "Footnote" : (title || "Source");
}

function sourceDisplayCitation(src) {
  const citation = String(src && src.citation || "").trim();
  return /^dach\s*:/i.test(citation) ? "" : citation;
}

function stripSourcePreview(html) {
  const d = document.createElement("div");
  d.innerHTML = window.cleanMayanosMarkup ? window.cleanMayanosMarkup(html) : String(html || "");
  d.querySelectorAll("[data-overlay]").forEach((e) => e.remove());
  return (d.textContent || "").replace(/\s+/g, " ").trim();
}

function sourceDateLabel(dates) {
  if (!Array.isArray(dates) || !dates.length) return "";
  const fmt = (n) => n < 0 ? `${Math.abs(n)} BCE` : `${n} CE`;
  return dates.length > 1 ? `${fmt(dates[0])}-${fmt(dates[dates.length - 1])}` : fmt(dates[0]);
}

function sourceQuoteCandidates(src, lang, anchorSegment) {
  const keys = lang === "he"
    ? ["quoteHe", "anchorTextHe", "anchorText"]
    : ["quoteEn", "anchorTextEn", "anchorText"];
  const out = [];
  keys.forEach((key) => {
    const value = src && src[key];
    (Array.isArray(value) ? value : [value]).forEach((v) => {
      const text = (window.cleanMayanosMarkup ? window.cleanMayanosMarkup(v) : String(v || "")).replace(/‹\d+›/g, "").replace(/\s+/g, " ").trim();
      if (text.length >= 14 && !out.includes(text)) out.push(text);
    });
  });
  if (anchorSegment) {
    const segText = (window.cleanMayanosMarkup ? window.cleanMayanosMarkup(lang === "he" ? anchorSegment.he : anchorSegment.en) : String((lang === "he" ? anchorSegment.he : anchorSegment.en) || ""))
      .replace(/‹\d+›/g, "")
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (segText.length >= 14 && !out.includes(segText)) out.push(segText);
  }
  return out;
}

function sourceQuoteChunks(candidates) {
  const chunks = [];
  candidates.forEach((candidate) => {
    chunks.push(candidate);
    const words = candidate.split(/\s+/).filter(Boolean);
    for (let size = Math.min(14, words.length); size >= 4; size--) {
      for (let i = 0; i + size <= words.length && chunks.length < 80; i++) {
        const chunk = words.slice(i, i + size).join(" ").replace(/^[,.;:()[\]"'“”]+|[,.;:()[\]"'“”]+$/g, "");
        if (chunk.length >= 18 && !chunks.includes(chunk)) chunks.push(chunk);
      }
    }
  });
  return chunks.sort((a, b) => b.length - a.length);
}

function normalizedMatchText(text) {
  const raw = String(text || "");
  let value = "";
  const map = [];
  let lastWasSpace = false;
  const pushSpace = (rawIndex) => {
    if (!value || lastWasSpace) return;
    value += " ";
    map.push(rawIndex);
    lastWasSpace = true;
  };
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (/[\u0591-\u05C7]/.test(ch)) continue;
    if (/\s/.test(ch)) { pushSpace(i); continue; }
    if (/[.,;:!?()[\]{}"'“”‘’״׳־–—-]/.test(ch)) { pushSpace(i); continue; }
    value += ch.toLowerCase();
    map.push(i);
    lastWasSpace = false;
  }
  return { value: value.trim(), map };
}

function normalizedQuoteRange(text, chunk) {
  const hay = normalizedMatchText(text);
  const needle = normalizedMatchText(chunk).value;
  if (!needle || needle.length < 12) return null;
  const at = hay.value.indexOf(needle);
  if (at < 0) return null;
  const start = hay.map[at];
  const end = hay.map[Math.min(hay.map.length - 1, at + needle.length - 1)];
  return Number.isFinite(start) && Number.isFinite(end) ? [start, end + 1] : null;
}

function sourceQuoteRange(text, candidates) {
  const raw = String(text || "");
  const lower = raw.toLowerCase();
  for (const chunk of sourceQuoteChunks(candidates)) {
    const needle = chunk.toLowerCase();
    const at = lower.indexOf(needle);
    if (at >= 0) return [at, at + chunk.length];
    const normalized = normalizedQuoteRange(raw, chunk);
    if (normalized) return normalized;
  }
  return null;
}

function renderSourceQuoteText(text, src, lang, anchorSegment) {
  const raw = window.cleanMayanosMarkup ? window.cleanMayanosMarkup(text) : String(text || "");
  const range = sourceQuoteRange(raw, sourceQuoteCandidates(src, lang, anchorSegment));
  if (!range) return raw;
  return [
    raw.slice(0, range[0]),
    <mark key="quote" className="source-quote-hit">{raw.slice(range[0], range[1])}</mark>,
    raw.slice(range[1]),
  ];
}

function SourceCard({ src, expanded, onToggle, onAdd, onSaveFolder, onFocusAnchor, compact, anchorSegment, collected }) {
  const c = typeVar(src.type);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [previewRetry, setPreviewRetry] = useState(0);
  useEffect(() => { setPreview(null); setPreviewError(false); }, [src.id, src.srcRef]);
  useEffect(() => {
    if (!expanded || (!src.related && !src.footnote)) return;
    let live = true;
    // Footnotes keep their original text; a located citation gets a separate
    // excerpt beneath it. Related cards show real text rather than a ref label.
    const candidates = sourceNavigationCandidates(src, window.SOURCES || [], window.SICHA || {})
      .filter(ref => /\d/.test(ref) && !/^(?:source|footnote)$/i.test(ref));
    if (!candidates.length) return;
    setLoadingPreview(true); setPreviewError(false);
    (async () => {
      for (const ref of candidates.slice(0, 3)) {
        if (!live) return;
        const text = await loadSourcePreview(ref).catch(() => null);
        if (!live) return;
        if (!isLocatedSource(text)) continue;
        const segments = sourcePreviewSegments(text, ref);
        const he = segments.map(segment => stripSourcePreview(segment.he)).filter(Boolean).join(" ");
        const en = segments.map(segment => stripSourcePreview(segment.en)).filter(Boolean).join(" ");
        if (he || en) { setPreview({ he, en, ref: segments.length === 1 ? segments[0].ref : ref }); return; }
      }
      if (live) setPreviewError(true);
    })().finally(() => { if (live) setLoadingPreview(false); });
    return () => { live = false; };
  }, [expanded, src.id, src.srcRef, src.related, src.footnote, previewRetry]);
  const heText = src.related ? preview?.he || "" : src.he;
  const enText = src.related ? preview?.en || "" : src.en;
  const meta = [
    src.linkType ? src.linkType.replace(/_/g, " ") : "",
    sourceDateLabel(src.compDate),
    src.anchorCount > 1 ? `${src.anchorCount} linked segments` : "",
  ].filter(Boolean);
  return (
    <div className={"srccard" + (expanded ? " is-open" : "")} onClick={onToggle}
      style={{ borderLeft: `3px solid ${c}` }}>
      <div className="srccard-head">
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span className="srccard-n" style={{ background: c }}>{src.n}</span>
          <div style={{ minWidth: 0 }}>
            <div className="srccard-title">{sourceDisplayTitle(src)} <span className="srccard-cite">{sourceDisplayCitation(src)}</span></div>
            <div className="srccard-rel"><TypeDot type={src.type} size={6} /> {src.type} · {src.relationship}</div>
          </div>
        </div>
        <Icon d="chevd" size={16} style={{ color: "var(--text-3)", transform: expanded ? "rotate(180deg)" : "none", transition: "transform .25s" }} />
      </div>
      {expanded && (
        <div className="srccard-body" onClick={(e) => e.stopPropagation()}>
          {meta.length > 0 && (
            <div className="srccard-meta">
              {meta.map((m) => <span key={m}>{m}</span>)}
            </div>
          )}
          {loadingPreview && <p className="srccard-loading">Loading source preview...</p>}
          {heText && <p lang="he" dir="rtl" className="heb srccard-he">{renderSourceQuoteText(heText, src, "he", anchorSegment)}</p>}
          {enText && <p className="srccard-en serif">{renderSourceQuoteText(enText, src, "en", anchorSegment)}</p>}
          {src.footnote && preview && <div className="srccard-cited-passage">
            <div className="srccard-cited-label"><Icon d="quote" size={13} /> {preview.ref}</div>
            {preview.he && <p lang="he" dir="rtl" className="heb srccard-he">{renderSourceQuoteText(preview.he, src, "he", anchorSegment)}</p>}
            {preview.en && <p className="srccard-en serif">{renderSourceQuoteText(preview.en, src, "en", anchorSegment)}</p>}
          </div>}
          {previewError && <div className="srccard-preview-error" role="status">The source preview could not load. <button type="button" className="mini-btn" onClick={() => setPreviewRetry(value => value + 1)}>Try again</button></div>}
          {!loadingPreview && !previewError && !heText && !enText && <p className="srccard-en serif">Open the source to read the full text.</p>}
          <div className="srccard-actions">
            <button className={`mini-btn srccard-save${collected ? " is-saved" : ""}`} disabled={collected}
              onClick={(e) => { e.stopPropagation(); onAdd && onAdd(src); }}>
              <Icon d={collected ? "check" : "plus"} size={13} /> {collected ? "Saved for sheet" : "Save for source sheet"}
            </button>
            {onSaveFolder && <button className="mini-btn" onClick={(e) => { e.stopPropagation(); onSaveFolder(src); }}><Icon d="bookmark" size={13} /> Save to folder</button>}
            {src.anchorRef && <button className="mini-btn srccard-anchor-btn" onClick={(e) => { e.stopPropagation(); onFocusAnchor && onFocusAnchor(src.anchorRef, src); }}><Icon d="back" size={13} /> In text</button>}
            <button className="mini-btn" onClick={(e) => { e.stopPropagation(); window.openSource ? window.openSource(src) : (window.openPalette && window.openPalette()); }}><Icon d="external" size={13} /> Open source</button>
            <button className="mini-btn" onClick={(e) => { e.stopPropagation(); window.openComparePair && window.openComparePair(src); }}><Icon d="compare" size={13} /> Compare</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SourceGraph — radial source map (SVG), used on home + reader ──────────────
// Center node = the sicha; ring nodes = sources colored by type.
// Animations: lines draw in, nodes spring-pop (staggered), center breathes,
// idle twinkle, and a ripple + line-highlight on hover. All visible-at-rest.
function SourceGraph({ sources, height = 360, active, onHover, center }) {
  const map = useMemo(() => window.createSourceMap({sources, center}), [sources, center]);
  return <div className="source-map-preview">
    <div className="source-map-preview-canvas" style={{height}}><SourceMapPro compact map={map} mode="radial" edgeStyle="line" labels="hover" selected={active} onSelect={onHover} /></div>
    <SourceEraLegend map={map}/>
  </div>;
}

// ── LayoutPicker — 3 reading layouts (stacked · aligned · parallel columns) ──
const LAYOUT_ICONS = {
  stacked: (<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="4" y="6" width="16" height="2.2" rx="1.1" /><rect x="4" y="11" width="16" height="2.2" rx="1.1" /><rect x="4" y="16" width="10" height="2.2" rx="1.1" /></svg>),
  split: (<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="7" width="7" height="2" rx="1" /><rect x="3" y="11" width="5.5" height="2" rx="1" />
    <rect x="14" y="7" width="7" height="2" rx="1" /><rect x="14" y="11" width="7" height="2" rx="1" /><rect x="14" y="15" width="5" height="2" rx="1" /></svg>),
  columns: (<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3" y="6" width="7" height="2" rx="1" /><rect x="3" y="10" width="7" height="2" rx="1" /><rect x="3" y="14" width="7" height="2" rx="1" /><rect x="3" y="18" width="5" height="2" rx="1" />
    <rect x="14" y="6" width="7" height="2" rx="1" /><rect x="14" y="10" width="7" height="2" rx="1" /><rect x="14" y="14" width="7" height="2" rx="1" /><rect x="14" y="18" width="5" height="2" rx="1" /></svg>),
};
const LAYOUT_META = [
  ["stacked", "Stacked", "One flowing column"],
  ["split", "Aligned", "English left · Hebrew right"],
  ["columns", "Columns", "Hebrew left · English right"],
];
function LayoutPicker({ value, onChange }) {
  return (
    <div className="layout-pick" role="tablist" aria-label="Reading layout">
      {LAYOUT_META.map(([k, label, title]) => (
        <button key={k} role="tab" className={"layout-btn" + (value === k ? " on" : "")} title={`${label} — ${title}`}
          onClick={() => onChange(k)} aria-label={label} aria-selected={value === k}>{LAYOUT_ICONS[k]}</button>
      ))}
    </div>
  );
}

// Hebrew numerals for section and daf labels (סעיף ד, דף י״ג), matching the
// numbering used in the contents picker. Shared by the reader and the home page.
const HE_ONES = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
const HE_TENS = ["", "י", "כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ"];
const HE_HUNDREDS = ["", "ק", "ר", "ש", "ת", "תק", "תר", "תש", "תת", "תתק"];
function heNumeral(value) {
  const n = Number(value) || 0;
  if (n <= 0 || n > 999) return String(value || "");
  let out = HE_HUNDREDS[Math.floor(n / 100) % 10] || "";
  const ten = Math.floor(n / 10) % 10, one = n % 10;
  if (ten === 1 && one === 5) out += "טו";
  else if (ten === 1 && one === 6) out += "טז";
  else out += HE_TENS[ten] + HE_ONES[one];
  return out.length > 1 ? `${out.slice(0, -1)}״${out.slice(-1)}` : out;
}

// One control, two skies: the knob carries the sun across into a moon, the
// track turns from day gold to night, and stars fade in after dark. Warm is
// the light mode. Where there is room the switch is shown with its name; the
// phone header collapses it to the same square control as the other icons.
function ThemeToggle({ direction = "beis", onDirection, caption = false }) {
  const dark = direction === "atlas";
  const next = dark ? "beis" : "atlas";
  const label = dark ? "Switch to warm mode" : "Switch to dark mode";
  const toggle = (
    <button type="button" className={"theme-toggle" + (dark ? " is-dark" : "")}
      role="switch" aria-checked={dark} aria-label={label} title={label}
      onClick={() => onDirection && onDirection(next)}>
      <span className="tt-stars" aria-hidden="true"><i /><i /><i /></span>
      <span className="tt-knob" aria-hidden="true">
        <span className="tt-face tt-face-day"><Icon d="sun" size={14} sw={1.8} /></span>
        <span className="tt-face tt-face-night"><Icon d="moon" size={14} sw={1.8} /></span>
      </span>
      <span className="tt-glyph" aria-hidden="true"><Icon d={dark ? "moon" : "sun"} size={18} sw={1.8} /></span>
    </button>
  );
  if (!caption) return toggle;
  return (
    <span className="theme-toggle-wrap">
      {toggle}
      <span className="theme-toggle-caption">{dark ? "Dark" : "Warm"}</span>
    </span>
  );
}

Object.assign(window, { Icon, Ic, Brand, TypeDot, SourceCard, SourceGraph, typeVar, LayoutPicker, LAYOUT_META, heNumeral, ThemeToggle });

window.useFullSichaPreview = function useFullSichaPreview(ref) {
  const [state, setState] = React.useState({sources:[],count:0,loading:true,error:false});
  React.useEffect(() => {
    let active = true;
    // With nothing to preview there is nothing to wait for, so never leave the
    // caller stuck on "Loading full-text connections…".
    if (!ref || !window.loadWholeSichaSources) {
      setState((current) => (current.loading || current.sources.length || current.error
        ? { sources: [], count: 0, loading: false, error: false }
        : current));
      return;
    }
    setState({sources:[],count:0,loading:true,error:false});
    window.loadWholeSichaSources(ref).then(result => {
      if (active) setState({sources:result.sources,count:result.refs.length,loading:false,error:false});
    }).catch(() => { if (active) setState({sources:[],count:0,loading:false,error:true}); });
    return () => { active = false; };
  }, [ref]);
  return state;
};
